import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useComplexFilters } from '../../hooks/useComplexFilters';
import { getAllAudiences, getAudienceById, deleteAudience, updateAudience, createAudience } from '../../api/audience';
import { useNavigate } from 'react-router-dom';
import AudienceModal from '../../components/automation/AudienceModal';

const AudienceSelection = ({ onSelectAudience, preselectedAudienceId, selectedAudience }) => {
  console.log('AudienceSelection rendered with:', { 
    preselectedAudienceId, 
    selectedAudience: selectedAudience ? {
      id: selectedAudience.id,
      name: selectedAudience.name,
      complex_filter_slug: selectedAudience.complex_filter_slug
    } : null
  });
  const [audiences, setAudiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalAudience, setModalAudience] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAudienceId, setSelectedAudienceId] = useState(null);
  const [selectedComplexFilterSlug, setSelectedComplexFilterSlug] = useState(null);
  const { filters: complexFilters, loading: loadingComplexFilters, error: errorComplexFilters } = useComplexFilters();
  const initialRenderRef = useRef(false);
  const hasSetSelectedRef = useRef(false);
  const navigate = useNavigate();

  console.log('AudienceSelection rendered with preselectedAudienceId:', preselectedAudienceId);
  console.log('Current selectedAudienceId state:', selectedAudienceId);

  // Fetch audiences on component mount
  useEffect(() => {
    fetchAudiences();
  }, []);

  // Define handleSelectComplexFilter before it's used in the useEffect hook
  const handleSelectComplexFilter = useCallback((filter) => {
    console.log('Complex filter selected:', filter.slug);
    
    // CRITICAL: Clear regular audience selection IMMEDIATELY and synchronously
    setSelectedAudienceId(null);
    setSelectedComplexFilterSlug(filter.slug);
    
    // Notify parent with only the complex filter data
    onSelectAudience({
      type: 'complex_filter', // Add type indicator
      complex_filter_slug: filter.slug,
      name: filter.name,
      description: filter.description,
      // Explicitly ensure no regular audience properties
      id: undefined,
      groups: undefined
    });
  }, [onSelectAudience]);

  // Update the useEffect that handles selection in AudienceSelection.jsx
  useEffect(() => {
    // Check for complex filter in multiple possible locations
    const complexFilterSlug = selectedAudience?.complex_filter_slug;
    
    // If we have both a complex filter slug and the filters are loaded
    if (complexFilterSlug && complexFilters?.length) {
      console.log('Found complex filter slug to select:', complexFilterSlug);
      
      // Clear regular audience selection to avoid conflicts
      setSelectedAudienceId(null);
      
      // Set complex filter as selected
      setSelectedComplexFilterSlug(complexFilterSlug);
      
      // Find matching filter for further actions if needed
      const matchingFilter = complexFilters.find(f => f.slug === complexFilterSlug);
      if (matchingFilter) {
        console.log('Found matching complex filter:', matchingFilter.name);
        
        // Select the complex filter if it wasn't already selected
        if (!initialRenderRef.current) {
          handleSelectComplexFilter(matchingFilter);
          initialRenderRef.current = true;
        }
      }
      
      return; // Exit early to prevent regular audience selection
    }
    
    // If no complex filter is selected, then handle regular audience selection
    if (selectedComplexFilterSlug && !complexFilterSlug) {
      // If we had a complex filter selected but now should use a regular audience,
      // clear the complex filter selection
      setSelectedComplexFilterSlug(null);
    }
    
    // The audience data could come from either preselectedAudienceId or selectedAudience.id
    const targetAudienceId = preselectedAudienceId || selectedAudience?.id;
    
    if (targetAudienceId && audiences.length > 0 && !loading) {
      console.log('Trying to highlight regular audience with ID:', targetAudienceId);
      
      // Find matching audience
      let matchingAudience = audiences.find(a => 
        a.id === targetAudienceId || a.audience_id === targetAudienceId
      );
      
      if (matchingAudience) {
        console.log('Found matching audience to highlight:', matchingAudience.filter_name || matchingAudience.name);
        
        // Set audience as selected
        setSelectedAudienceId(matchingAudience.id);
        hasSetSelectedRef.current = true;
      } else {
        console.warn('No matching audience found in loaded audiences for ID:', targetAudienceId);
        
        // Still set the ID for highlighting
        setSelectedAudienceId(targetAudienceId);
        hasSetSelectedRef.current = true;
      }
    }
  }, [audiences, complexFilters, loading, preselectedAudienceId, selectedAudience, selectedComplexFilterSlug, handleSelectComplexFilter]);

  // Helper function to check if ANY audience is currently selected
  const hasAnySelection = () => {
    return !!(selectedAudienceId || selectedComplexFilterSlug);
  };

  // Helper function to get current selection info for parent validation
  const getCurrentSelection = () => {
    if (selectedComplexFilterSlug) {
      const filter = complexFilters?.find(f => f.slug === selectedComplexFilterSlug);
      return {
        type: 'complex_filter',
        slug: selectedComplexFilterSlug,
        name: filter?.name || 'Unknown Filter'
      };
    } else if (selectedAudienceId) {
      const audience = audiences.find(a => a.id === selectedAudienceId);
      return {
        type: 'regular_audience', 
        id: selectedAudienceId,
        name: audience?.filter_name || audience?.name || 'Unknown Audience'
      };
    }
    return null;
  };

  // Expose selection status to parent component via callback
  useEffect(() => {
    const currentSelection = getCurrentSelection();
    // Call a validation callback if provided by parent
    if (typeof onSelectAudience === 'function') {
      // We could add a second callback specifically for validation status
      // For now, we'll rely on the selection data itself
    }
  }, [selectedAudienceId, selectedComplexFilterSlug, complexFilters, audiences]);

  const fetchAudiences = async () => {
    try {
      setLoading(true);
      setError(null);
      const audienceData = await getAllAudiences();
      console.log('Fetched audiences:', audienceData);

      // IF development mode, log data for debugging
      if (process.env.NODE_ENV === 'development') {
        console.table(audienceData.map(a => ({
          name: a.filter_name || a.name,
          id: a.id,
          audience_id: a.audience_id || 'N/A',
          visibility: a.visibility
        })));
      }

      if (Array.isArray(audienceData) && audienceData.length > 0) {
        setAudiences(audienceData);
        
        // If we have a preselected audience ID, auto-select it
        if (preselectedAudienceId) {
          console.log('Looking for preselected audience in fetched data:', preselectedAudienceId);
          
          // Check for direct ID match first
          let preselectedAudience = audienceData.find(a => a.id === preselectedAudienceId);
          
          // If not found, also look for match in audience_id field
          if (!preselectedAudience) {
            preselectedAudience = audienceData.find(a => a.audience_id === preselectedAudienceId);
          }
          
          // Log all audience IDs for debugging
          console.log('Available audience IDs:', audienceData.map(a => ({ 
            id: a.id, 
            audience_id: a.audience_id || null,
            filter_name: a.filter_name || a.name
          })));
          
          if (preselectedAudience) {
            console.log('Found preselected audience:', preselectedAudience);
            
            // Set the selected ID for UI highlighting
            setSelectedAudienceId(preselectedAudience.id);
            hasSetSelectedRef.current = true;
            
            // Auto-fetch the full audience data and notify the parent
            handleSelectAudience(preselectedAudience, true);
          } else {
            console.warn('Preselected audience not found in fetched data:', preselectedAudienceId);
            
            // IMPORTANT: Even if we don't find a match, still set the ID for highlighting
            setSelectedAudienceId(preselectedAudienceId);
            hasSetSelectedRef.current = true;
            
            // Special case: Try using a direct autoload of the audience by ID if nothing matches
            try {
              console.log('Attempting direct audience fetch by ID');
              const directAudience = await getAudienceById(preselectedAudienceId);
              if (directAudience && (directAudience.id || directAudience.audience_id)) {
                console.log('Successfully loaded audience directly:', directAudience);
                
                // Create a simplified audience object for selection
                const simplifiedAudience = {
                  id: directAudience.id || directAudience.audience_id,
                  audience_id: directAudience.audience_id || directAudience.id,
                  filter_name: directAudience.filter_name || directAudience.name || 'Unnamed Audience',
                  visibility: directAudience.visibility || 'private'
                };
                
                // Update the audiences list to include this audience
                setAudiences(prev => [...prev, simplifiedAudience]);
                
                // Set as selected
                setSelectedAudienceId(simplifiedAudience.id);
                hasSetSelectedRef.current = true;
                
                // Select it
                handleSelectAudience(simplifiedAudience, true);
              }
            } catch (directErr) {
              console.error('Failed to load audience directly:', directErr);
            }
          }
        }
      } else {
        setAudiences([]);
      }
    } catch (err) {
      console.error('Failed to fetch audiences:', err);
      setError('Failed to load audiences.');
    } finally {
      setLoading(false);
    }
  };

  // New function to fetch and transform audience data by ID
  const fetchAndTransformAudience = async (audienceId) => {
    try {
      console.log('Fetching detailed audience data for ID:', audienceId);
      const audienceData = await getAudienceById(audienceId);
      console.log('Raw audience data from API for selection:', JSON.stringify(audienceData, null, 2));
      
      return transformApiAudienceData(audienceData);
    } catch (err) {
      console.error('Failed to fetch audience details:', err);
      return null;
    }
  };

  // Update the handleSelectAudience function
  const handleSelectAudience = async (audience, isPreselected = false) => {
    console.log('handleSelectAudience called with:', audience, 'isPreselected:', isPreselected);
    if (!audience || !audience.id) {
      console.error("Invalid audience object provided to handleSelectAudience");
      return;
    }
    
    // CRITICAL: Clear complex filter selection IMMEDIATELY and synchronously
    setSelectedComplexFilterSlug(null);
    setSelectedAudienceId(audience.id);
    hasSetSelectedRef.current = true;
    
    // Then fetch the full details including conditions
    const detailedAudience = await fetchAndTransformAudience(audience.id);
    if (detailedAudience) {
      console.log('Detailed audience data selected:', detailedAudience);
      // Pass the complete audience data to the parent component
      const audienceData = {
        type: 'regular_audience', // Add type indicator
        ...detailedAudience,
        id: audience.id,
        filter_name: detailedAudience.name || audience.filter_name || audience.name
      };
      // Explicitly remove any complex_filter_slug to avoid conflicts
      delete audienceData.complex_filter_slug;
      
      onSelectAudience(audienceData);
    } else {
      // Fallback to basic data if fetch fails
      const fallbackAudience = {
        type: 'regular_audience', // Add type indicator
        id: audience.id,
        name: audience.filter_name || audience.name,
        filter_name: audience.filter_name || audience.name,
        groups: [
          { type: 'ALL', conditions: [] },
          { type: 'ANY', conditions: [] }
        ]
      };
      console.log('Using fallback audience data:', fallbackAudience);
      onSelectAudience(fallbackAudience);
    }
  };

  // Transform API audience data to the format expected by our components
  const transformApiAudienceData = (audienceData) => {
    // Create the base transformed structure
    const transformedData = {
      name: audienceData.filter_name,
      id: audienceData.audience_id || audienceData.id,
      audience_id: audienceData.audience_id || audienceData.id,
      visibility: audienceData.visibility,
      groups: []
    };
    
    // Check if we have valid conditions
    const hasValidMatchAll = audienceData.match_all && 
                             Array.isArray(audienceData.match_all) && 
                             audienceData.match_all.length > 0 &&
                             audienceData.match_all[0].conditions &&
                             Array.isArray(audienceData.match_all[0].conditions) &&
                             audienceData.match_all[0].conditions.length > 0;
                             
    const hasValidMatchAny = audienceData.match_any && 
                             Array.isArray(audienceData.match_any) && 
                             audienceData.match_any.length > 0 &&
                             audienceData.match_any[0].conditions &&
                             Array.isArray(audienceData.match_any[0].conditions) &&
                             audienceData.match_any[0].conditions.length > 0;
    
    // Process match_all conditions
    if (hasValidMatchAll) {
      const allGroup = {
        type: 'ALL',
        conditions: []
      };
      
      // Extract conditions from match_all
      audienceData.match_all.forEach((group, idx) => {
        if (group.conditions && Array.isArray(group.conditions)) {
          group.conditions.forEach((condition, condIdx) => {
            if (condition.field) {
              const parts = condition.field.split('.');
              if (parts.length === 2) {
                const [recordType, fieldName] = parts;
                
                const transformedCondition = {
                  recordType,
                  fieldName,
                  operator: condition.operator,
                  value: condition.value,
                  conditionGroup: 'ALL',
                  id: `all-${Date.now()}-${condIdx}-${Math.random().toString(36).substring(2, 9)}`
                };
                
                allGroup.conditions.push(transformedCondition);
              }
            }
          });
        }
      });
      
      if (allGroup.conditions.length > 0) {
        transformedData.groups.push(allGroup);
      }
    }
    
    // Process match_any conditions
    if (hasValidMatchAny) {
      const anyGroup = {
        type: 'ANY',
        conditions: []
      };
      
      // Extract conditions from match_any
      audienceData.match_any.forEach((group, idx) => {
        if (group.conditions && Array.isArray(group.conditions)) {
          group.conditions.forEach((condition, condIdx) => {
            if (condition.field) {
              const parts = condition.field.split('.');
              if (parts.length === 2) {
                const [recordType, fieldName] = parts;
                
                const transformedCondition = {
                  recordType,
                  fieldName,
                  operator: condition.operator,
                  value: condition.value,
                  conditionGroup: 'ANY',
                  id: `any-${Date.now()}-${condIdx}-${Math.random().toString(36).substring(2, 9)}`
                };
                
                anyGroup.conditions.push(transformedCondition);
              }
            }
          });
        }
      });
      
      if (anyGroup.conditions.length > 0) {
        transformedData.groups.push(anyGroup);
      }
    }
    
    // Ensure ALL and ANY groups exist even if empty
    if (!transformedData.groups.some(g => g.type === 'ALL')) {
      transformedData.groups.push({ type: 'ALL', conditions: [] });
    }
    if (!transformedData.groups.some(g => g.type === 'ANY')) {
      transformedData.groups.push({ type: 'ANY', conditions: [] });
    }
    
    return transformedData;
  };

  const openModal = async (audienceId, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      console.log('Opening modal for audience ID:', audienceId);
      
      const audienceData = await getAudienceById(audienceId);
      console.log('Raw audience data from API:', JSON.stringify(audienceData, null, 2));
      
      const transformedData = transformApiAudienceData(audienceData);
      
      console.log('Final transformed data for AudienceBuilder:', JSON.stringify(transformedData, null, 2));
      
      // Use the renamed state variable here
      setModalAudience(transformedData);
      setShowModal(true);
    } catch (err) {
      console.error('Failed to fetch audience details:', err);
      setError('Failed to load audience details.');
    }
  };
  
  const closeModal = () => {
    setShowModal(false);
    setModalAudience(null); // Use the renamed variable
  };

  const handleDeleteAudience = async (audienceId) => {
    try {
      console.log('Deleting audience:', audienceId);
      await deleteAudience(audienceId);
      
      // Refresh audience list after deletion
      fetchAudiences();
      
      return true;
    } catch (err) {
      console.error('Failed to delete audience:', err);
      throw err;
    }
  };

  const handleUpdateAudience = async (updatedAudience) => {
    try {
      console.log('Updating audience with full data:', JSON.stringify(updatedAudience, null, 2));
      
      // Send the update request with properly formatted data
      // The updatedAudience object should already be properly formatted by AudienceModal
      await updateAudience(updatedAudience);
      
      // Refresh audiences list after update
      fetchAudiences();
      
      return true;
    } catch (err) {
      console.error('Failed to update audience:', err);
      throw err;
    }
  };

  // Handler for creating new audience
  const handleCreateAudience = async (audienceData) => {
    try {
      console.log('Creating new audience with data:', audienceData);
      await createAudience(audienceData);
      setShowCreateModal(false);
      fetchAudiences();
    } catch (err) {
      console.error('Failed to create audience:', err);
      throw err;
    }
  };

  // Display loading state
  if (loading) {
    return <div>Loading audiences...</div>;
  }

  // Display error state if present
  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>
        <button
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#5c6bc0',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
          onClick={fetchAudiences}
        >
          Retry
        </button>
      </div>
    );
  }

  // Determine if we should show the debug panel
  const showDebugPanel = process.env.NODE_ENV === 'development';

  // Helper function to check if an audience should be highlighted
  const isAudienceSelected = (audience) => {
    // Check direct ID match
    if (audience.id === selectedAudienceId || audience.id === preselectedAudienceId) {
      return true;
    }
    
    // Check audience_id match
    if (audience.audience_id && (
        audience.audience_id === selectedAudienceId || 
        audience.audience_id === preselectedAudienceId)) {
      return true;
    }
    
    return false;
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h2>Select or Create an Audience</h2>
      
      {/* Selection Status Display */}
      <div style={{
        marginBottom: '1rem',
        padding: '0.75rem',
        borderRadius: '4px',
        backgroundColor: hasAnySelection() ? '#e8f5e8' : '#fff3cd',
        border: hasAnySelection() ? '1px solid #4caf50' : '1px solid #ffc107',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          {hasAnySelection() ? (
            <span style={{ color: '#2e7d2e', fontWeight: 'bold' }}>
              ✓ Selected: {getCurrentSelection()?.name}
              {getCurrentSelection()?.type === 'complex_filter' && ' (System Filter)'}
            </span>
          ) : (
            <span style={{ color: '#856404', fontWeight: 'bold' }}>
              ⚠️ Please select exactly one audience to continue
            </span>
          )}
        </div>
        {hasAnySelection() && (
          <span style={{ 
            fontSize: '0.8rem', 
            color: '#666',
            fontStyle: 'italic'
          }}>
            Click another audience to switch selection
          </span>
        )}
      </div>

      <button
        style={{
          marginBottom: '1rem',
          padding: '0.75rem 1.5rem',
          fontSize: '1rem',
          cursor: 'pointer',
          backgroundColor: '#5c6bc0',
          color: 'white',
          borderRadius: '4px',
          border: 'none',
        }}
        onClick={() => setShowCreateModal(true)}
      >
        + Create New Audience
      </button>

      {/* Debug information panel */}
      {showDebugPanel && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px',
          fontSize: '0.8rem',
          border: '1px solid #ddd'
        }}>
          <p><strong>Debug Panel:</strong></p>
          <p><strong>Selected Audience ID:</strong> {selectedAudienceId || 'none'}</p>
          <p><strong>Selected Complex Filter:</strong> {selectedComplexFilterSlug || 'none'}</p>
          <p><strong>Preselected Audience ID:</strong> {preselectedAudienceId || 'none'}</p>
          <p><strong>Has Any Selection:</strong> {hasAnySelection() ? 'Yes' : 'No'}</p>
          <p><strong>Current Selection:</strong> {getCurrentSelection() ? 
            `${getCurrentSelection().type} - ${getCurrentSelection().name}` : 'None'}</p>
          <p><strong>Has set selected:</strong> {hasSetSelectedRef.current ? 'Yes' : 'No'}</p>
          <p><strong>Audiences count:</strong> {audiences.length}</p>
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        {audiences.length > 0 ? (
          audiences.map((audience) => {
            // Check if this audience is selected in multiple ways
            const isSelected = isAudienceSelected(audience) && !selectedComplexFilterSlug;
            // Define highlight styles based on selection status
            const borderStyle = isSelected ? '2px solid #5c6bc0' : '1px solid #ddd';
            const bgColor = isSelected ? '#e8eaf6' : '#fff';
            const transform = isSelected ? 'translateY(-2px)' : 'none';
            const boxShadow = isSelected
              ? '0 0 10px rgba(92,107,192,0.3)'
              : '2px 2px 6px rgba(0,0,0,0.1)';
            console.log(`Audience ${audience.filter_name || audience.name} (${audience.id}): isSelected=${isSelected}`);
            return (
              <div
                key={audience.id}
                style={{
                  position: 'relative',
                  border: borderStyle,
                  borderRadius: '8px',
                  padding: '1rem',
                  width: '250px',
                  boxShadow,
                  backgroundColor: bgColor,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
                  transform,
                }}
                onClick={() => handleSelectAudience(audience)}
              >
                {/* Mini debug badge in corner if highlighted */}
                {isSelected && (
                  <div style={{
                    position: 'absolute',
                    top: '0.25rem',
                    right: '0.25rem',
                    backgroundColor: '#5c6bc0',
                    color: 'white',
                    fontSize: '0.6rem',
                    padding: '2px 5px',
                    borderRadius: '3px'
                  }}>Selected</div>
                )}

                <h3>{audience.filter_name || audience.name}</h3>
                <p><strong>Visibility:</strong> {audience.visibility}</p>
                {audience.created_at && (
                  <p><strong>Created:</strong> {new Date(audience.created_at).toLocaleDateString()}</p>
                )}

                {isSelected && (
                  <p style={{ color: '#5c6bc0', fontWeight: 'bold' }}>Currently Selected</p>
                )}

                {audience.visibility !== 'system' ? (
                  <button
                    style={{
                      marginTop: '0.5rem',
                      backgroundColor: '#5c6bc0',
                      color: 'white',
                      borderRadius: '4px',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                    }}
                    onClick={(e) => openModal(audience.id, e)}
                  >
                    View / Edit
                  </button>
                ) : (
                  <button
                    style={{
                      marginTop: '0.5rem',
                      backgroundColor: '#5c6bc0',
                      color: 'white',
                      borderRadius: '4px',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      cursor: 'not-allowed',
                    }}
                    disabled
                  >
                    View Only
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <div>No audiences available</div>
        )}
      </div>

      {/* System Complex Filters Section */}
      <h3 style={{ marginTop: '2rem' }}>System Complex Filters</h3>
      {loadingComplexFilters && <div>Loading filters...</div>}
      {errorComplexFilters && (
        <div style={{ color: 'red' }}>
          {errorComplexFilters.message || 'Failed to load system filters.'}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
        {complexFilters && complexFilters.map((filter) => {
          const isSelected = selectedComplexFilterSlug === filter.slug;
          return (
            <div
              key={filter.slug}
              style={{
                position: 'relative',
                border: isSelected ? '2px solid #5c6bc0' : '1px solid #ddd',
                borderRadius: '8px',
                padding: '1rem',
                width: '250px',
                boxShadow: isSelected ? '0 0 10px rgba(92,107,192,0.3)' : '2px 2px 6px rgba(0,0,0,0.1)',
                backgroundColor: isSelected ? '#e8eaf6' : '#f5f5f5',
                cursor: 'pointer',
                transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
                transform: isSelected ? 'translateY(-2px)' : 'none',
              }}
              onClick={() => handleSelectComplexFilter(filter)}
            >
              {isSelected && (
                <div style={{
                  position: 'absolute',
                  top: '0.25rem',
                  right: '0.25rem',
                  backgroundColor: '#5c6bc0',
                  color: 'white',
                  fontSize: '0.6rem',
                  padding: '2px 5px',
                  borderRadius: '3px'
                }}>Selected</div>
              )}
              <h3>{filter.name}</h3>
              <p>{filter.description}</p>
              <p><strong>Slug:</strong> {filter.slug}</p>
              {isSelected && (
                <p style={{ color: '#5c6bc0', fontWeight: 'bold' }}>Currently Selected</p>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <AudienceModal
          open={showModal}
          onClose={closeModal}
          audience={modalAudience} // Use the renamed variable
          onDelete={handleDeleteAudience}
          onUpdate={handleUpdateAudience}
        />
      )}
      {showCreateModal && (
        <AudienceModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          audience={{
            name: '',
            visibility: 'private',
            groups: [
              { type: 'ALL', conditions: [] },
              { type: 'ANY', conditions: [] }
            ]
          }}
          onSave={handleCreateAudience}
          mode="create"
        />
      )}
    </div>
  );
};

export default AudienceSelection;